import { useEffect, useState } from "react";
import { PaperPlaneTilt, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const Field = ({ label, required, children }) => (
  <label className="block space-y-1.5">
    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--s-mute)]">
      {label}
      {required && <span className="ml-1 text-[color:var(--s-gold)]">*</span>}
    </span>
    <span className="lead-field relative block">{children}</span>
  </label>
);

// "Send to my designer": emails the slab card (photo, specs, link) plus the shopper's note.
export const DesignerDialog = ({ open, onClose, item, itemKey, testId = "designer-dialog" }) => {
  const [f, setF] = useState({ designer_email: "", designer_name: "", your_name: "", your_email: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((v) => ({ ...v, ...patch }));

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  useEffect(() => {
    if (open && item && !f.note) set({ note: `I love ${item.color} for our project — can we work this into the plan?` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.color]);

  if (!open || !item) return null;

  const send = async (e) => {
    e.preventDefault();
    if (!f.designer_email.trim()) return toast.error("Add your designer's email");
    setBusy(true);
    try {
      const res = await fetch(`${API}/public/showroom/item/${itemKey}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...f, your_email: f.your_email.trim() || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not send");
      toast.success(`Sent to ${f.designer_name.trim() || f.designer_email}`);
      onClose();
    } catch (err) {
      toast.error(err.message || "Could not send");
    }
    setBusy(false);
  };

  return (
    <div
      className="site site-overlay fixed inset-0 z-[90] flex items-end justify-center bg-[#1a1a18]/60 p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Send to my designer"
      data-testid={testId}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form onSubmit={send} className="lead-embedded relative max-h-[92vh] w-full max-w-xl overflow-y-auto bg-white p-6 shadow-2xl sm:p-10">
        <button type="button" onClick={onClose} data-testid={`${testId}-close`} aria-label="Close" className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--s-line)] text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]">
          <X size={16} weight="bold" />
        </button>
        <p className="s-micro !text-[10px] text-[color:var(--s-gold)]">Designer handoff</p>
        <h3 className="s-hx mt-3 pr-12 text-2xl sm:text-3xl" data-testid={`${testId}-title`}>
          Send <strong>{item.color}</strong> to your designer.
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-[color:var(--s-mute)]">
          They get the slab card — photo, size, finish and a link back to this page — with your note on top.
        </p>

        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          <Field label="Designer's email" required>
            <input data-testid={`${testId}-designer-email`} type="email" required autoComplete="off" value={f.designer_email} onChange={(e) => set({ designer_email: e.target.value })} placeholder="jordan@studio.com" className="w-full" />
          </Field>
          <Field label="Designer's name">
            <input data-testid={`${testId}-designer-name`} value={f.designer_name} onChange={(e) => set({ designer_name: e.target.value })} placeholder="Jordan" className="w-full" />
          </Field>
          <Field label="Your name">
            <input data-testid={`${testId}-your-name`} value={f.your_name} onChange={(e) => set({ your_name: e.target.value })} placeholder="Sam Rivera" className="w-full" />
          </Field>
          <Field label="Your email (cc)">
            <input data-testid={`${testId}-your-email`} type="email" value={f.your_email} onChange={(e) => set({ your_email: e.target.value })} placeholder="sam@example.com" className="w-full" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Your note">
              <textarea data-testid={`${testId}-note`} value={f.note} onChange={(e) => set({ note: e.target.value })} rows={4} maxLength={1200} className="w-full" />
            </Field>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-6">
          <p className="text-xs text-[color:var(--s-mute)]">We never share your addresses — this is a one-off email.</p>
          <button type="submit" disabled={busy} data-testid={`${testId}-send`} className="s-btn w-full sm:w-auto">
            <PaperPlaneTilt size={16} weight="bold" /> {busy ? "Sending…" : "Send the slab"}
          </button>
        </div>
      </form>
    </div>
  );
};
